import React, { useEffect, useState } from "react"
import { Grid, MenuItem, Select } from "@material-ui/core"
import { DateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers"
import DateFnsUtils from "@date-io/date-fns"
import { ptBR } from "date-fns/locale"
import { ConditionOperator, DateFilterState, defaultDateOperators } from "@/protocols/ClientCatalogFilterProtocol"

const operatorToDateFilterOptions: Partial<Record<ConditionOperator, Array<keyof DateFilterState>>> = {
	between: ["dateFrom", "dateTo"],
	greater_than: ["dateFrom"],
	less_than: ["dateTo"]
}

interface DateFilterOptionsProps {
	operator: ConditionOperator
	dateFrom?: Date | null
	dateTo?: Date | null
	onChange: (next: DateFilterState) => void
	onlyBetween?: boolean
}

const DateFilterOptions: React.FC<DateFilterOptionsProps> = ({
	operator,
	dateFrom,
	dateTo,
	onChange,
	onlyBetween = false
}) => {
	const [dateOptions, setDateOptions] = useState<DateFilterState>({
		operator: onlyBetween ? "between" : operator,
		dateFrom: dateFrom ?? new Date(),
		dateTo: dateTo ?? new Date()
	})

	const dateOperatorOptions = operatorToDateFilterOptions[dateOptions.operator] || []

	const operatorsList = onlyBetween
		? defaultDateOperators.filter(operator => operator.value === "between")
		: defaultDateOperators

	const handleOperatorChange = (operator: ConditionOperator) => {
		const operatorToDefaultDateFilterOptions: Partial<Record<ConditionOperator, DateFilterState>> = {
			between: { operator: "between", dateFrom: new Date(), dateTo: new Date() },
			greater_than: { operator: "greater_than", dateFrom: new Date() },
			less_than: { operator: "less_than", dateTo: new Date() }
		}
		setDateOptions(operatorToDefaultDateFilterOptions[operator] as DateFilterState)
	}

	const handleFieldChange = <K extends keyof DateFilterState>(
		field: K,
		value: Date | null | ConditionOperator
	) => {
		if (field === "operator") {
			handleOperatorChange(value as ConditionOperator)
		} else {
			setDateOptions(prev => ({ ...prev, [field]: value }))
		}
	}

	useEffect(() => {
		onChange(dateOptions)
	}, [dateOptions])

	return (
		<Grid container direction="row" alignItems="center" spacing={1}>
			<Grid item xs={4}>
				<Select
					fullWidth
					variant="outlined"
					value={dateOptions.operator}
					onChange={({ target }) => handleOperatorChange(target.value as ConditionOperator)}
					disabled={onlyBetween}
				>
					{operatorsList.map(option => (
						<MenuItem key={option.value} value={option.value}>
							{option.label}
						</MenuItem>
					))}
				</Select>
			</Grid>

			{dateOperatorOptions.map(dateOperator => (
				<Grid item xs={dateOperatorOptions.length === 2 ? 4 : 8} key={dateOperator}>
					<MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBR}>
						<DateTimePicker
							ampm={false}
							format="dd/MM/yyyy HH:mm"
							inputVariant="outlined"
							views={["year", "month", "date", "hours", "minutes"]}
							variant="inline"
							size="small"
							fullWidth
							value={dateOptions[dateOperator]}
							onChange={date => handleFieldChange(dateOperator, date)}
							autoOk
						/>
					</MuiPickersUtilsProvider>
				</Grid>
			))}
		</Grid>
	)
}

export default DateFilterOptions
